import { Dispatch, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import * as amplitude from "@amplitude/analytics-browser";
import { Button, OTPStep } from '@unified-checkout/design-system';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { useGlobalStore } from '@app/dashboard/store/global';
import { Spinner } from '@app/dashboard/assets/icons';
import { loginVerifyOtp, loginResendOtp } from '@app/dashboard/services/outer_layout/login';
import type { ILoginOtpStep } from '@app/dashboard/types/outerLayout/login';
import { handleAfterLogin } from '../utils';
import { PROFILE } from '@app/dashboard/constants/profile';
import { useInnerLayoutLogic } from '@app/dashboard/views/layout/InnerLayout/utils';
import styles from "../login.module.scss";

const usersTypes = [PROFILE.TYPE.MERCHANT, PROFILE.TYPE.MERCHANT_STAFF];

const SUBMIT_BTN_ID = 'login-otp-submit-btn';

const OtpStep = (
  {
    loading,
    setLoading,
    redirectToRegister,
    otpToken
  }: ILoginOtpStep
) => {
  const navigate = useNavigate();
  const { setIsAuthenticated } = useGlobalStore();
  const { handleSwitchDashboard } = useInnerLayoutLogic();
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const [otp, setOtp] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [serverErrMsg, setServerErrMsg] = useState('');
  const [serverSuccessMsg, setServerSuccessMsg] = useState('');
  const [otpResetTimerKey, setOtpResetTimerKey] = useState(0);


  useEffect(() => {
    setServerErrMsg("");
    setServerSuccessMsg("");
  }, [otp]);

  const onValidHandler = (valid: boolean) => {
    setIsValid(valid);
  };

  const onResendOTP = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    loginResendOtp(otpToken)
      .then(() => {
        setOtpResetTimerKey(prevKey => prevKey + 1);
        setIsValid(false);
        setServerSuccessMsg(t('form.otpResent'));
      })
      .catch((error) => {
        const message = error.response?.data?.message;

        setServerErrMsg(message || t('errors.somethingWentWrong'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOtpSubmit = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    loginVerifyOtp(otp, otpToken)
      .then(async (response) => {
        amplitude.track('sign_in_otp');
        const data = response?.data;
        const accessToken = data.access;
        const type = data?.profile.profile_type;
        const { acq_partner } = data.profile;
        if (!acq_partner && usersTypes.includes(type)) {
          await handleAfterLogin(response, navigate, setServerErrMsg);
          setIsAuthenticated(true);
        } else {
          // any user rather than Merchant or MerchantStaff
          await handleSwitchDashboard(accessToken);
          setLoading(false);
        }
      })
      .catch((error) => {
        const response = error.response;
        const data = response?.data;
        const message = data?.message;

        const wrongOtpMsg = "Wrong OTP";

        response && setServerErrMsg(message === wrongOtpMsg ? t('outerLayout.form.errors.wrongOtp') : t('outerLayout.form.errors.otpExpired'));
      })
      .finally(() => {
        setLoading(false);
      });

  };

  return (
    <>
      <OTPStep
        otp={otp}
        setOtp={setOtp}
        otpResetTimerKey={otpResetTimerKey}
        onValid={onValidHandler}
        onResendOTP={onResendOTP}
        resendText={t('form.resendCodeIn')}
        resendBtnText={t('form.resend')}
        resendTimer={150}
        length={6}
        loading={loading}
      />
      <div className='form__submit'>
        {serverErrMsg && !serverSuccessMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}
        {serverSuccessMsg && !serverErrMsg && <div className='form__submit__server-success'>{serverSuccessMsg}</div>}

        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('form.submit')}
          leadingIcon={loading ? Spinner : undefined}
          disabled={!isValid || loading}
          onClickHandler={handleOtpSubmit}
        />
      </div>

      <div className={styles["login-form__dont-have-account"]}>
        <span className={styles["login-form__dont-have-account__text"]}>{t('outerLayout.form.dontHaveAcc')}</span>
        <div className={styles["login-form__dont-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.signUp')}
            onClickHandler={redirectToRegister}
          />
        </div>
      </div>
    </>
  )
}

export default OtpStep;